import { formatDate } from "@/lib/format";
import { useState, useMemo } from "react";
import { Link, useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { accountStatementsApi, type AccountStatementSummaryDto, type IssueStatementRequest } from "@/lib/clients-accounts";
import { ListPage } from "@/archetypes/ListPage";
import { Button } from "@/components/ui/button";
import { DetailModal } from "@/components/detail-modal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

export function AccountStatementsListPage() {
  const { id = "0" } = useParams();
  const accountId = Number(id);
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [forDate, setForDate] = useState(new Date().toISOString().slice(0, 10));
  const [issueMethod, setIssueMethod] = useState<"Email" | "Print" | "Manual">("Email");

  const { data, isLoading } = useQuery({
    queryKey: ["account-statements", accountId],
    queryFn: () => accountStatementsApi.list(accountId),
  });

  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const rows = useMemo(() => data?.items ?? [], [data?.items]);
  const visibleRows = useMemo(() => {
    if (!sort) return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const valueFor = (s: AccountStatementSummaryDto): string | number | null => {
      switch (sort.columnId) {
        case "statementNo":   return s.statementNo;
        case "statementDate": return s.statementDate;
        case "opening":       return s.openingBalance;
        case "closing":       return s.closingBalance;
        default:              return null;
      }
    };
    return [...rows].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [rows, sort]);

  const cols: DataGridColumn<AccountStatementSummaryDto>[] = [
    { id: "statementNo",   header: "Statement #", sortable: true, accessor: (s) => s.statementNo },
    {
      id: "statementDate", header: "Date", sortable: true,
      accessor: (s) => formatDate(s.statementDate),
    },
    {
      id: "period", header: "Period", sortable: false,
      accessor: (s) =>
        `${formatDate(s.periodFromDate)} — ${formatDate(s.periodToDate)}`,
    },
    {
      id: "opening", header: "Opening", sortable: true, align: "right",
      accessor: (s) => fmt.format(s.openingBalance),
    },
    {
      id: "closing", header: "Closing", sortable: true, align: "right",
      accessor: (s) => fmt.format(s.closingBalance),
    },
    { id: "issued", header: "Issued", sortable: false, accessor: (s) => s.issueMethod ?? "—" },
    {
      id: "actions", header: "", sortable: false,
      accessor: (s) => (
        <Link to={`/accounts/${accountId}/statements/${s.statementId}`} className="underline">
          Open
        </Link>
      ),
    },
  ];

  const issueMut = useMutation({
    mutationFn: (req: IssueStatementRequest) => accountStatementsApi.issue(accountId, req),
    onSuccess: () => {
      toast.success("Statement enqueued");
      setDrawerOpen(false);
      void qc.invalidateQueries({ queryKey: ["account-statements", accountId] });
    },
    onError: () => toast.error("Failed to enqueue statement"),
  });

  return (
    <ListPage
      title="Statements"
      backTo={`/accounts/${accountId}`}
      primaryAction={<Button onClick={() => setDrawerOpen(true)}>Generate new statement</Button>}
    >
      <DataGrid
        rows={visibleRows}
        columns={cols}
        rowKey={(s) => s.statementId}
        totalRows={rows.length}
        pageIndex={0}
        pageSize={rows.length || 1}
        loading={isLoading}
        emptyHeadline="No statements issued yet."
        onPageChange={() => {}}
        onSortChange={setSort}
      />

      <DetailModal open={drawerOpen} onOpenChange={setDrawerOpen} title="Generate new statement">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            issueMut.mutate({ forDate: new Date(forDate).toISOString(), outstandingOverride: null, issueMethod });
          }}
        >
          <div>
            <Label htmlFor="for-date">For date</Label>
            <Input id="for-date" type="date" value={forDate} onChange={(e) => setForDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="method">Issue method</Label>
            <Select value={issueMethod} onValueChange={(v) => setIssueMethod(v as typeof issueMethod)}>
              <SelectTrigger id="method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="Print">Print</SelectItem>
                <SelectItem value="Manual">Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={issueMut.isPending}>
            {issueMut.isPending ? "Enqueuing…" : "Generate"}
          </Button>
        </form>
      </DetailModal>
    </ListPage>
  );
}
